import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import React, { useContext, useEffect, useState } from "react";
import {
  fetchQuery,
  useFragment,
  useMutation,
  useRelayEnvironment,
} from "react-relay";
import { InternBrandOnboardingGetDescoperTaskStatusQuery } from "./__generated__/InternBrandOnboardingGetDescoperTaskStatusQuery.graphql";
import { InternBrandOnboardingSaveDescoperSelectorMutation } from "./__generated__/InternBrandOnboardingSaveDescoperSelectorMutation.graphql";
import { InternBrandOnboardingStartTaskMutation } from "./__generated__/InternBrandOnboardingStartTaskMutation.graphql";
import { InternBrandOnboarding_brand$key } from "./__generated__/InternBrandOnboarding_brand.graphql";
import CardSection from "../../../components/CardSection";
import SnackbarContext from "../../../contexts/SnackbarContext";

const brandFragment = graphql`
  fragment InternBrandOnboarding_brand on BrandType {
    id
    domain
  }
`;
const startDescoperMutation = graphql`
  mutation InternBrandOnboardingStartTaskMutation(
    $input: StartDescoperTaskInput!
  ) {
    startDescoperTask(input: $input) {
      taskKey
      status
      message
    }
  }
`;

const getDescoperTaskStatusQuery = graphql`
  query InternBrandOnboardingGetDescoperTaskStatusQuery($taskKey: String!) {
    descoperTaskStatus(taskKey: $taskKey) {
      status
      progress
      currentStep
      message
      error
      result {
        pageUrl
        initialScreenshot {
          topScreenshotUrl
          bottomScreenshotUrl
        }
        validationScreenshot {
          topScreenshotUrl
          bottomScreenshotUrl
        }
        validated {
          type
          selector
          confidence
          reasons
          x
          y
          width
          height
        }
        invalidated {
          type
          selector
          confidence
          reasons
          x
          y
          width
          height
        }
        error
      }
    }
  }
`;

const saveDescoperSelectorMutation = graphql`
  mutation InternBrandOnboardingSaveDescoperSelectorMutation(
    $input: SaveDescoperSelectorInput!
  ) {
    saveDescoperSelector(input: $input) {
      success
      message
    }
  }
`;

interface InternBrandOnboardingProps {
  brand: InternBrandOnboarding_brand$key;
}

const InternBrandOnboarding: React.FC<InternBrandOnboardingProps> = ({
  brand: brandKey,
}) => {
  const environment = useRelayEnvironment();
  const brand = useFragment(brandFragment, brandKey);
  const [isPending, setIsPending] = useState(false);
  const [startDescoper, isStarting] =
    useMutation<InternBrandOnboardingStartTaskMutation>(startDescoperMutation);
  const [taskKey, setTaskKey] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [statusData, setStatusData] = useState<any>(null);
  const [pageUrl, setPageUrl] = useState<string>(brand.domain);
  const [saveSelector, isSaving] =
    useMutation<InternBrandOnboardingSaveDescoperSelectorMutation>(
      saveDescoperSelectorMutation
    );
  const snackbarContext = useContext(SnackbarContext);
  const handleStartDescoper = () => {
    setIsPending(true);
    startDescoper({
      variables: {
        input: {
          brandId: brand.id,
          pageUrl: pageUrl.trim().length > 0 ? pageUrl.trim() : null,
        },
      },
      onCompleted: (response) => {
        if (response.startDescoperTask) {
          setTaskKey(response.startDescoperTask.taskKey);
          setPolling(true);
          snackbarContext?.openSnackbar(
            "Page Descoper started successfully",
            "success"
          );
        }
      },
      onError: (error) => {
        console.error("Error starting descoper:", error);
        snackbarContext?.openSnackbar("Page Descoper failed to start", "error");
      },
    });
    setTimeout(() => {
      setIsPending(false);
    }, 1000);
  };

  const handleSaveSelector = (selector: string) => {
    saveSelector({
      variables: {
        input: {
          brandId: brand.id,
          selector: selector,
        },
      },
      onCompleted: (response) => {
        if (response.saveDescoperSelector?.success) {
          console.log(
            "Selector saved successfully:",
            response.saveDescoperSelector.message
          );
          snackbarContext?.openSnackbar(
            response.saveDescoperSelector.message,
            "success"
          );
        } else {
          console.error(
            "Failed to save selector:",
            response.saveDescoperSelector?.message
          );
          snackbarContext?.openSnackbar(
            response.saveDescoperSelector?.message,
            "error"
          );
        }
      },
      onError: (error) => {
        console.error("Error saving selector:", error);
        snackbarContext?.openSnackbar("Error saving selector", "error");
      },
    });
  };

  // Polling for status updates using the proper Relay query
  useEffect(() => {
    if (!taskKey || !polling) return;

    const pollInterval = setInterval(async () => {
      try {
        const data =
          await fetchQuery<InternBrandOnboardingGetDescoperTaskStatusQuery>(
            environment,
            getDescoperTaskStatusQuery,
            { taskKey },
            { fetchPolicy: "network-only" }
          ).toPromise();

        if (data?.descoperTaskStatus) {
          setStatusData(data.descoperTaskStatus);

          if (
            data.descoperTaskStatus.status === "SUCCESS" ||
            data.descoperTaskStatus.status === "FAILURE"
          ) {
            setPolling(false);
          }
        }
      } catch (error) {
        console.error("Error polling status:", error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [taskKey, polling, environment]);

  const isRunning =
    statusData?.status === "RUNNING" || statusData?.status === "PENDING";
  const isComplete = statusData?.status === "SUCCESS";
  const hasFailed = statusData?.status === "FAILURE";

  // Content for the card
  const cardContent = (
    <Box>
      <Box mb={2}>
        <TextField
          fullWidth
          label="Page URL (optional)"
          placeholder={`https://${brand.domain}/specific-page`}
          value={pageUrl || ""}
          onChange={(e) => setPageUrl(e.target.value)}
          disabled={isStarting || isRunning || isPending}
          helperText="Leave empty to descope the main domain page"
          size="small"
        />
      </Box>

      {statusData && (
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Status: {statusData.status} - {statusData.message}
          </Typography>

          {isRunning && (
            <Box mt={1}>
              <LinearProgress
                variant="determinate"
                value={statusData.progress}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption">
                {statusData.currentStep} ({statusData.progress}%)
              </Typography>
            </Box>
          )}

          {hasFailed && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {statusData.error || "Analysis failed"}
            </Alert>
          )}

          {isComplete && statusData.result && (
            <Box mt={2}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Analysis completed successfully!
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                Results Summary:
              </Typography>
              <ul>
                <li>
                  Validated elements: {statusData.result.validated?.length || 0}
                </li>
                <li>
                  Invalidated elements:{" "}
                  {statusData.result.invalidated?.length || 0}
                </li>
              </ul>

              {/* Screenshots Section */}
              {statusData.result.initialScreenshot && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Screenshots
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={3}>
                    {/* Initial Screenshots */}
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        Initial Screenshots
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <Box>
                          <Typography variant="caption" display="block" mb={1}>
                            Top Screenshot
                          </Typography>
                          <Box
                            component="img"
                            src={
                              statusData.result.initialScreenshot
                                .topScreenshotUrl
                            }
                            alt="Initial top screenshot"
                            sx={{
                              maxWidth: "300px",
                              width: "100%",
                              height: "auto",
                              border: "2px solid #e0e0e0",
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": {
                                border: "2px solid #1976d2",
                              },
                            }}
                            onClick={() =>
                              window.open(
                                statusData.result.initialScreenshot
                                  .topScreenshotUrl,
                                "_blank"
                              )
                            }
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" display="block" mb={1}>
                            Bottom Screenshot
                          </Typography>
                          <Box
                            component="img"
                            src={
                              statusData.result.initialScreenshot
                                .bottomScreenshotUrl
                            }
                            alt="Initial bottom screenshot"
                            sx={{
                              maxWidth: "300px",
                              width: "100%",
                              height: "auto",
                              border: "2px solid #e0e0e0",
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": {
                                border: "2px solid #1976d2",
                              },
                            }}
                            onClick={() =>
                              window.open(
                                statusData.result.initialScreenshot
                                  .bottomScreenshotUrl,
                                "_blank"
                              )
                            }
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Validation Screenshots */}
                    {statusData.result.validationScreenshot && (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "bold" }}
                        >
                          Validation Screenshots
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Box>
                            <Typography
                              variant="caption"
                              display="block"
                              mb={1}
                            >
                              Top Screenshot (After Validation)
                            </Typography>
                            <Box
                              component="img"
                              src={
                                statusData.result.validationScreenshot
                                  .topScreenshotUrl
                              }
                              alt="Validation top screenshot"
                              sx={{
                                maxWidth: "300px",
                                width: "100%",
                                height: "auto",
                                border: "2px solid #4caf50",
                                borderRadius: 1,
                                cursor: "pointer",
                                "&:hover": {
                                  border: "2px solid #2e7d32",
                                },
                              }}
                              onClick={() =>
                                window.open(
                                  statusData.result.validationScreenshot
                                    .topScreenshotUrl,
                                  "_blank"
                                )
                              }
                            />
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              display="block"
                              mb={1}
                            >
                              Bottom Screenshot (After Validation)
                            </Typography>
                            <Box
                              component="img"
                              src={
                                statusData.result.validationScreenshot
                                  .bottomScreenshotUrl
                              }
                              alt="Validation bottom screenshot"
                              sx={{
                                maxWidth: "300px",
                                width: "100%",
                                height: "auto",
                                border: "2px solid #4caf50",
                                borderRadius: 1,
                                cursor: "pointer",
                                "&:hover": {
                                  border: "2px solid #2e7d32",
                                },
                              }}
                              onClick={() =>
                                window.open(
                                  statusData.result.validationScreenshot
                                    .bottomScreenshotUrl,
                                  "_blank"
                                )
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Show detailed results */}
              {statusData.result.validated?.length > 0 && (
                <Box mt={3}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Validated Elements:
                  </Typography>
                  {statusData.result.validated.map(
                    (element: any, index: number) => (
                      <Box
                        key={index}
                        p={2}
                        borderRadius={1}
                        mb={1}
                        sx={{ border: "1px solid #4caf50" }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box flex={1}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {index + 1}. {element.type} - {element.selector}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Confidence: {element.confidence} | Position: (
                              {element.x}px, {element.y}px) | Size:{" "}
                              {element.width}px×{element.height}px
                            </Typography>
                            {element.reasons.map(
                              (reason: string, reasonIndex: number) => (
                                <Typography
                                  key={reasonIndex}
                                  variant="caption"
                                  color="textSecondary"
                                  display="block"
                                >
                                  - {reason}
                                </Typography>
                              )
                            )}
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSaveSelector(element.selector)}
                            disabled={isSaving}
                            sx={{
                              ml: 2,
                              minWidth: "80px",
                              fontSize: "0.75rem",
                            }}
                          >
                            {isSaving ? "Saving..." : "Save"}
                          </Button>
                        </Box>
                      </Box>
                    )
                  )}
                </Box>
              )}

              {/* Invalidated elements */}
              {statusData.result.invalidated?.length > 0 && (
                <Box mt={3}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Invalidated Elements:
                  </Typography>
                  {statusData.result.invalidated.map(
                    (element: any, index: number) => (
                      <Box
                        key={index}
                        p={2}
                        bgcolor="error.light"
                        borderRadius={1}
                        mb={1}
                        sx={{ border: "1px solid #f44336" }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {index + 1}. {element.type} - {element.selector}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Confidence: {element.confidence} | Position: (
                          {element.x}, {element.y}) | Size: {element.width}×
                          {element.height}
                        </Typography>
                        {element.reasons?.length > 0 && (
                          <Typography variant="caption" color="textSecondary">
                            Reasons: {element.reasons.join(", ")}
                          </Typography>
                        )}
                      </Box>
                    )
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  // Actions for the card
  const cardActions = (
    <Button
      variant="contained"
      onClick={handleStartDescoper}
      disabled={isStarting || isRunning || isPending}
      startIcon={
        isStarting || isRunning || isPending ? (
          <CircularProgress size={20} />
        ) : undefined
      }
      style={{
        minWidth: "150px",
      }}
    >
      {isRunning ? "Descoping..." : "Start"}
    </Button>
  );

  return (
    <CardSection
      title="Page Descoper"
      subtitle="Scope down the brand's page to be optimized for rendering in the app"
      content={cardContent}
      actions={cardActions}
    />
  );
};

export default InternBrandOnboarding;
